/**
 * G21 product layout stability guard.
 * Keeps the native gallery visible and moves product accordions/details out of
 * the media column so no content remains beneath the product image or video.
 */
(() => {
  'use strict';

  const unstableClasses = [
    'g21-media-layout-recovery',
    'g21-media-visibility-recovery',
    'g21-media-rescue-active',
  ];

  const moveAdditionalBlocks = (scope = document) => {
    const sections = [];

    if (scope instanceof Element && scope.matches('.product-information')) {
      sections.push(scope);
    }

    if (scope.querySelectorAll) {
      sections.push(...scope.querySelectorAll('.product-information'));
    }

    sections.forEach((section) => {
      const productGrid = section.querySelector('product-component[data-product-grid-content]');
      const additional = section.querySelector('[data-g21-media-additional]');

      if (!productGrid || !additional) return;

      if (additional.parentElement !== section || productGrid.nextElementSibling !== additional) {
        productGrid.insertAdjacentElement('afterend', additional);
      }

      additional.classList.add('g21-product-additional--full-width');
      additional.style.setProperty('width', '100%');
      additional.style.setProperty('min-width', '0');
      additional.style.setProperty('grid-column', '1 / -1');
      additional.style.setProperty('margin-top', 'clamp(20px, 3vw, 40px)');
    });
  };

  const stabilize = (scope = document) => {
    const pages = [];
    if (scope instanceof Element && scope.matches('.g21-product-page')) pages.push(scope);
    if (scope.querySelectorAll) pages.push(...scope.querySelectorAll('.g21-product-page'));

    pages.forEach((page) => {
      unstableClasses.forEach((className) => page.classList.remove(className));

      page.querySelectorAll('media-gallery').forEach((gallery) => {
        gallery.hidden = false;
        gallery.removeAttribute('aria-hidden');
        gallery.style.removeProperty('display');
        gallery.style.removeProperty('visibility');
        gallery.style.removeProperty('opacity');
      });

      page.querySelectorAll('[data-g21-media-fallback]').forEach((fallback) => {
        fallback.hidden = true;
        fallback.setAttribute('aria-hidden', 'true');
      });
    });

    moveAdditionalBlocks(scope);
  };

  let correcting = false;
  const observer = new MutationObserver((mutations) => {
    if (correcting) return;

    const needsCorrection = mutations.some((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) return true;
      if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') return false;

      const target = mutation.target;
      return target instanceof Element &&
        target.matches('.g21-product-page') &&
        unstableClasses.some((className) => target.classList.contains(className));
    });

    if (!needsCorrection) return;

    correcting = true;
    stabilize(document);
    queueMicrotask(() => { correcting = false; });
  });

  const start = () => {
    stabilize(document);

    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    [100, 500, 1200, 2600].forEach((delay) => {
      window.setTimeout(() => stabilize(document), delay);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => stabilize(event.target));
})();
